import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDashboard, useMileage } from '../hooks/useApi'
import { formatCurrency, formatDateShort, getMonthYear, getCurrentDateString } from '../lib/utils'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Skeleton } from '../components/ui/skeleton'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog'
import { toast } from 'sonner'
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign,
  Camera,
  Truck,
  Calendar,
  FileText,
  ChevronRight,
  Receipt,
  Gauge,
  Play,
  Square,
  MapPin,
  Upload,
  PenLine,
  Mail
} from 'lucide-react'
import { cn } from '../lib/utils'

export default function Dashboard() {
  const [data, setData] = useState(null)
  const [mileageDialogOpen, setMileageDialogOpen] = useState(false)
  const [expenseDialogOpen, setExpenseDialogOpen] = useState(false)
  const [loadDialogOpen, setLoadDialogOpen] = useState(false)
  const [startOdometer, setStartOdometer] = useState('')
  const [endOdometer, setEndOdometer] = useState('')
  const [mileageNotes, setMileageNotes] = useState('')
  const { fetchDashboard, loading, error } = useDashboard()
  const { createEntry, updateEntry, loading: mileageLoading } = useMileage()
  const navigate = useNavigate()

  useEffect(() => {
    loadDashboard()
  }, [])

  const loadDashboard = async () => {
    try {
      const result = await fetchDashboard()
      setData(result)
    } catch (err) {
      console.error('Failed to load dashboard:', err)
    }
  }

  const handleStartMileage = async () => {
    if (!startOdometer) {
      toast.error('Please enter starting odometer')
      return
    }
    
    try {
      await createEntry({
        date: getCurrentDateString(),
        start_odometer: parseFloat(startOdometer),
        notes: mileageNotes || null
      })
      toast.success('Mileage tracking started!')
      setStartOdometer('')
      setMileageNotes('')
      setMileageDialogOpen(false)
      loadDashboard()
    } catch (err) {
      toast.error('Failed to start mileage tracking')
    }
  }

  const handleEndMileage = async () => {
    if (!endOdometer) {
      toast.error('Please enter ending odometer')
      return
    }
    
    const openEntry = data?.mileage?.open_entry
    if (!openEntry) return
    
    if (parseFloat(endOdometer) <= openEntry.start_odometer) {
      toast.error('Ending odometer must be greater than starting')
      return
    }
    
    try {
      await updateEntry(openEntry.id, {
        end_odometer: parseFloat(endOdometer),
        notes: mileageNotes || openEntry.notes
      })
      toast.success('Mileage recorded!')
      setEndOdometer('')
      setMileageNotes('')
      setMileageDialogOpen(false)
      loadDashboard()
    } catch (err) {
      toast.error('Failed to record mileage')
    }
  }

  if (loading && !data) {
    return <DashboardSkeleton />
  }

  const profit = data?.month?.profit ?? 0
  const isProfitable = profit >= 0
  const hasOpenMileage = data?.mileage?.open_entry
  const hasMileageData = (data?.month?.miles ?? 0) > 0

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard">
      {/* Header */}
      <div className="space-y-1">
        <h1 className="text-2xl font-bold tracking-tight font-['Barlow_Condensed']">
          DASHBOARD
        </h1>
        <p className="text-muted-foreground">{getMonthYear()}</p>
      </div>

      {/* Quick Actions - At the top for easy access */}
      <div>
        <h2 className="text-lg font-semibold mb-3 font-['Barlow_Condensed'] uppercase tracking-wide">
          Quick Actions
        </h2>
        <div className="grid grid-cols-4 gap-3">
          {/* Expenses Button - Yellow */}
          <Dialog open={expenseDialogOpen} onOpenChange={setExpenseDialogOpen}>
            <DialogTrigger asChild>
              <button
                className="quick-action"
                data-testid="quick-action-expenses"
              >
                <Receipt className="w-6 h-6 text-yellow-500" strokeWidth={1.5} />
                <span className="text-xs font-medium text-center">Add Expense</span>
              </button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Expense</DialogTitle>
                <DialogDescription>
                  Choose how you want to add an expense
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-3 py-4">
                <Button
                  variant="outline"
                  className="h-14 justify-start gap-3"
                  onClick={() => {
                    setExpenseDialogOpen(false)
                    navigate('/scanner?type=receipt')
                  }}
                  data-testid="expense-scan-btn"
                >
                  <Camera className="w-5 h-5 text-yellow-500" />
                  <div className="text-left">
                    <p className="font-medium">Scan Receipt</p>
                    <p className="text-xs text-muted-foreground">Use camera to scan</p>
                  </div>
                </Button>
                <Button
                  variant="outline"
                  className="h-14 justify-start gap-3"
                  onClick={() => {
                    setExpenseDialogOpen(false)
                    navigate('/scanner?type=receipt&upload=true')
                  }}
                  data-testid="expense-upload-btn"
                >
                  <Upload className="w-5 h-5 text-yellow-500" />
                  <div className="text-left">
                    <p className="font-medium">Upload Image</p>
                    <p className="text-xs text-muted-foreground">Upload from device</p>
                  </div>
                </Button>
                <Button
                  variant="outline"
                  className="h-14 justify-start gap-3"
                  onClick={() => {
                    setExpenseDialogOpen(false)
                    navigate('/scanner?type=receipt&email=true')
                  }}
                  data-testid="expense-email-btn"
                >
                  <Mail className="w-5 h-5 text-yellow-500" />
                  <div className="text-left">
                    <p className="font-medium">From Email</p>
                    <p className="text-xs text-muted-foreground">Paste email content</p>
                  </div>
                </Button>
                <Button
                  variant="outline"
                  className="h-14 justify-start gap-3"
                  onClick={() => {
                    setExpenseDialogOpen(false)
                    navigate('/expenses?new=true')
                  }}
                  data-testid="expense-manual-btn"
                >
                  <PenLine className="w-5 h-5 text-yellow-500" />
                  <div className="text-left">
                    <p className="font-medium">Manual Entry</p>
                    <p className="text-xs text-muted-foreground">Enter details manually</p>
                  </div>
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Add Load Button - Blue */}
          <Dialog open={loadDialogOpen} onOpenChange={setLoadDialogOpen}>
            <DialogTrigger asChild>
              <button
                className="quick-action"
                data-testid="quick-action-add-load"
              >
                <Truck className="w-6 h-6 text-blue-500" strokeWidth={1.5} />
                <span className="text-xs font-medium text-center">Add Load</span>
              </button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Load / Revenue</DialogTitle>
                <DialogDescription>
                  Choose how you want to add revenue
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-3 py-4">
                <Button
                  variant="outline"
                  className="h-14 justify-start gap-3"
                  onClick={() => {
                    setLoadDialogOpen(false)
                    navigate('/scanner?type=load')
                  }}
                  data-testid="load-scan-btn"
                >
                  <Camera className="w-5 h-5 text-blue-500" />
                  <div className="text-left">
                    <p className="font-medium">Scan Document</p>
                    <p className="text-xs text-muted-foreground">Use camera to scan rate con</p>
                  </div>
                </Button>
                <Button
                  variant="outline"
                  className="h-14 justify-start gap-3"
                  onClick={() => {
                    setLoadDialogOpen(false)
                    navigate('/scanner?type=load&upload=true')
                  }}
                  data-testid="load-upload-btn"
                >
                  <Upload className="w-5 h-5 text-blue-500" />
                  <div className="text-left">
                    <p className="font-medium">Upload Image</p>
                    <p className="text-xs text-muted-foreground">Upload from device</p>
                  </div>
                </Button>
                <Button
                  variant="outline"
                  className="h-14 justify-start gap-3"
                  onClick={() => {
                    setLoadDialogOpen(false)
                    navigate('/scanner?type=load&email=true')
                  }}
                  data-testid="load-email-btn"
                >
                  <Mail className="w-5 h-5 text-blue-500" />
                  <div className="text-left">
                    <p className="font-medium">From Email</p>
                    <p className="text-xs text-muted-foreground">Paste rate con email</p>
                  </div>
                </Button>
                <Button
                  variant="outline"
                  className="h-14 justify-start gap-3"
                  onClick={() => {
                    setLoadDialogOpen(false)
                    navigate('/loads?new=true')
                  }}
                  data-testid="load-manual-btn"
                >
                  <PenLine className="w-5 h-5 text-blue-500" />
                  <div className="text-left">
                    <p className="font-medium">Manual Entry</p>
                    <p className="text-xs text-muted-foreground">Enter details manually</p>
                  </div>
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Per Diem Button - Purple */}
          <button
            onClick={() => navigate('/per-diem')}
            className="quick-action"
            data-testid="quick-action-per-diem"
          >
            <Calendar className="w-6 h-6 text-purple-500" strokeWidth={1.5} />
            <span className="text-xs font-medium text-center">Per Diem</span>
          </button>

          {/* Reports Button - Green */}
          <button
            onClick={() => navigate('/reports')}
            className="quick-action"
            data-testid="quick-action-reports"
          >
            <FileText className="w-6 h-6 text-green-500" strokeWidth={1.5} />
            <span className="text-xs font-medium text-center">Reports</span>
          </button>
        </div>
      </div>

      {/* KPI Cards - Main Stats */}
      <div className="grid grid-cols-3 gap-3">
        {/* Revenue Card - Blue */}
        <Card className="card-interactive" data-testid="revenue-card">
          <CardContent className="p-3 lg:p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] lg:text-xs font-semibold uppercase tracking-wider text-muted-foreground font-['Barlow_Condensed']">
                REVENUE
              </span>
              <TrendingUp className="w-3 h-3 lg:w-4 lg:h-4 text-blue-500" />
            </div>
            <p className="text-lg lg:text-2xl font-bold kpi-value text-blue-500">
              {formatCurrency(data?.month?.revenue ?? 0, 0)}
            </p>
            <p className="text-[10px] lg:text-xs text-muted-foreground">
              {data?.month?.load_count ?? 0} loads
            </p>
          </CardContent>
        </Card>

        {/* Expenses Card - Yellow */}
        <Card className="card-interactive" data-testid="expenses-card">
          <CardContent className="p-3 lg:p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] lg:text-xs font-semibold uppercase tracking-wider text-muted-foreground font-['Barlow_Condensed']">
                EXPENSES
              </span>
              <TrendingDown className="w-3 h-3 lg:w-4 lg:h-4 text-yellow-500" />
            </div>
            <p className="text-lg lg:text-2xl font-bold kpi-value text-yellow-500">
              {formatCurrency(data?.month?.expenses ?? 0, 0)}
            </p>
            <p className="text-[10px] lg:text-xs text-muted-foreground">
              {data?.month?.expense_count ?? 0} items
            </p>
          </CardContent>
        </Card>

        {/* Profit Card - Green */}
        <Card 
          className="card-interactive border-green-500/30"
          data-testid="profit-card"
        >
          <CardContent className="p-3 lg:p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] lg:text-xs font-semibold uppercase tracking-wider text-muted-foreground font-['Barlow_Condensed']">
                PROFIT
              </span>
              <DollarSign className="w-3 h-3 lg:w-4 lg:h-4 text-green-500" />
            </div>
            <p className="text-lg lg:text-2xl font-bold kpi-value text-green-500">
              {formatCurrency(profit, 0)}
            </p>
            <p className="text-[10px] lg:text-xs text-muted-foreground">
              YTD: {formatCurrency(data?.ytd?.profit ?? 0, 0)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Per-Mile KPIs - Only show if mileage data exists */}
      {hasMileageData && (
        <div className="grid grid-cols-3 gap-3">
          {/* Revenue per mile - Blue */}
          <Card className="bg-secondary/50" data-testid="revenue-per-mile-card">
            <CardContent className="p-3">
              <div className="flex items-center gap-2 mb-1">
                <MapPin className="w-3 h-3 text-blue-500" />
                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground font-['Barlow_Condensed']">
                  $/MILE REV
                </span>
              </div>
              <p className="text-lg font-bold kpi-value text-blue-500">
                ${data?.month?.revenue_per_mile?.toFixed(2) ?? '0.00'}
              </p>
            </CardContent>
          </Card>

          {/* Cost per mile - Yellow */}
          <Card className="bg-secondary/50" data-testid="cost-per-mile-card">
            <CardContent className="p-3">
              <div className="flex items-center gap-2 mb-1">
                <MapPin className="w-3 h-3 text-yellow-500" />
                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground font-['Barlow_Condensed']">
                  $/MILE COST
                </span>
              </div>
              <p className="text-lg font-bold kpi-value text-yellow-500">
                ${data?.month?.cost_per_mile?.toFixed(2) ?? '0.00'}
              </p>
            </CardContent>
          </Card>

          {/* Profit per mile - Green */}
          <Card className="bg-secondary/50" data-testid="profit-per-mile-card">
            <CardContent className="p-3">
              <div className="flex items-center gap-2 mb-1">
                <MapPin className="w-3 h-3 text-green-500" />
                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground font-['Barlow_Condensed']">
                  $/MILE NET
                </span>
              </div>
              <p className="text-lg font-bold kpi-value text-green-500">
                ${data?.month?.profit_per_mile?.toFixed(2) ?? '0.00'}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Mileage Tracker Card - Below summary boxes */}
      <Card className={cn(
        "border-2",
        hasOpenMileage ? "border-amber-500/50 bg-amber-500/5" : "border-dashed border-muted-foreground/30"
      )}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={cn(
                "w-10 h-10 rounded-full flex items-center justify-center",
                hasOpenMileage ? "bg-amber-500/20" : "bg-muted"
              )}>
                <Gauge className={cn("w-5 h-5", hasOpenMileage ? "text-amber-500" : "text-muted-foreground")} />
              </div>
              <div>
                {hasOpenMileage ? (
                  <>
                    <p className="font-medium text-amber-500">Trip in Progress</p>
                    <p className="text-sm text-muted-foreground">
                      Started: {data.mileage.open_entry.start_odometer.toLocaleString()} mi
                      {data.mileage.open_entry.notes && ` • ${data.mileage.open_entry.notes}`}
                    </p>
                  </>
                ) : (
                  <>
                    <p className="font-medium">Track Mileage</p>
                    <p className="text-sm text-muted-foreground">
                      {hasMileageData 
                        ? `${data.month.miles.toLocaleString()} miles this month`
                        : 'Optional - track by load, trip, or week'}
                    </p>
                  </>
                )}
              </div>
            </div>
            
            <Dialog open={mileageDialogOpen} onOpenChange={setMileageDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  variant={hasOpenMileage ? "default" : "outline"}
                  size="sm"
                  className={hasOpenMileage ? "bg-amber-500 hover:bg-amber-600" : ""}
                  data-testid="mileage-btn"
                >
                  {hasOpenMileage ? (
                    <>
                      <Square className="w-4 h-4 mr-1" />
                      End Trip
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4 mr-1" />
                      Start
                    </>
                  )}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>
                    {hasOpenMileage ? 'End Mileage Tracking' : 'Start Mileage Tracking'}
                  </DialogTitle>
                  <DialogDescription>
                    {hasOpenMileage 
                      ? 'Enter your ending odometer reading to record miles.'
                      : 'Enter your starting odometer reading. End it whenever you want.'}
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4 py-4">
                  {hasOpenMileage ? (
                    <>
                      <div className="p-3 bg-secondary rounded-lg">
                        <p className="text-sm text-muted-foreground">Starting Odometer</p>
                        <p className="text-xl font-bold font-mono">
                          {data.mileage.open_entry.start_odometer.toLocaleString()} mi
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label>Ending Odometer</Label>
                        <Input
                          type="number"
                          placeholder="e.g., 125500"
                          value={endOdometer}
                          onChange={(e) => setEndOdometer(e.target.value)}
                          className="h-12 text-lg font-mono"
                          data-testid="end-odometer-input"
                        />
                      </div>
                    </>
                  ) : (
                    <div className="space-y-2">
                      <Label>Starting Odometer</Label>
                      <Input
                        type="number"
                        placeholder="e.g., 125000"
                        value={startOdometer}
                        onChange={(e) => setStartOdometer(e.target.value)}
                        className="h-12 text-lg font-mono"
                        data-testid="start-odometer-input"
                      />
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    <Label>Notes (optional)</Label>
                    <Input
                      placeholder="e.g., Dallas run, Week 3"
                      value={mileageNotes}
                      onChange={(e) => setMileageNotes(e.target.value)}
                      className="h-12"
                      data-testid="mileage-notes-input"
                    />
                  </div>
                  
                  <Button
                    onClick={hasOpenMileage ? handleEndMileage : handleStartMileage}
                    disabled={mileageLoading}
                    className="w-full h-12 rounded-full font-semibold"
                    data-testid="save-mileage-btn"
                  >
                    {hasOpenMileage ? 'Record Miles' : 'Start Tracking'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Recent Expenses */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-['Barlow_Condensed'] uppercase tracking-wide">
                Recent Expenses
              </CardTitle>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/expenses')}
                data-testid="view-all-expenses"
              >
                View All <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {data?.recent_expenses?.length > 0 ? (
              data.recent_expenses.slice(0, 3).map((expense) => (
                <div key={expense.id} className="expense-item">
                  <div className="category-icon">
                    <Receipt className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{expense.merchant}</p>
                    <p className="text-sm text-muted-foreground">
                      {expense.category_name} • {formatDateShort(expense.date)}
                    </p>
                  </div>
                  <p className="font-semibold text-yellow-500">
                    -{formatCurrency(expense.amount)}
                  </p>
                </div>
              ))
            ) : (
              <p className="text-center text-muted-foreground py-8">
                No expenses yet. Add one from Quick Actions!
              </p>
            )}
          </CardContent>
        </Card>

        {/* Recent Loads */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-['Barlow_Condensed'] uppercase tracking-wide">
                Recent Loads
              </CardTitle>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/loads')}
                data-testid="view-all-loads"
              >
                View All <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {data?.recent_loads?.length > 0 ? (
              data.recent_loads.slice(0, 3).map((load) => (
                <div key={load.id} className="expense-item">
                  <div className="category-icon bg-blue-500/10">
                    <Truck className="w-5 h-5 text-blue-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">
                      {load.pickup_location} → {load.delivery_location}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {load.broker || 'Direct'} • {formatDateShort(load.pickup_date)}
                    </p>
                  </div>
                  <p className="font-semibold text-blue-500">
                    +{formatCurrency(load.rate)}
                  </p>
                </div>
              ))
            ) : (
              <p className="text-center text-muted-foreground py-8">
                No loads yet. Add one from Quick Actions!
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <Skeleton className="h-9 w-48" />
        <Skeleton className="h-5 w-32" />
      </div>
      <Skeleton className="h-20" />
      <div className="grid grid-cols-3 gap-3">
        <Skeleton className="h-24" />
        <Skeleton className="h-24" />
        <Skeleton className="h-24" />
      </div>
      <div>
        <Skeleton className="h-6 w-32 mb-3" />
        <div className="grid grid-cols-4 gap-3">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
      </div>
    </div>
  )
}
